import store from '../../store';
import ApiaField from '../ApiaField';
import ApiaForm from '../ApiaForm';
import Field from '../constants/Field';
// import ApiaUtil from '../ApiaUtil';
import { IApiaField } from '../types';
import { dataGridActions } from '../../store/dataGridSlice/dataGridSlice';
import EventEmitter from '../../utils/util/eventEmitter';
import { dataGridRefreshEvent } from '../../store/dataGridSlice/rows/refresh';
import { TApiaXMLResponse } from '../../store/dataGridSlice/types';
import { noNaN } from '../../utils/util';

export interface IDataGridField extends IApiaField {
  closeForm(): void;
}

export const datagridEmitter = new (class DatagridEmitter extends EventEmitter<{
  toggleDeleteButton: {
    rowIndex: number;
    isShown?: boolean;
  };
}> {})();

export default class DataGridField extends ApiaField implements IDataGridField {
  gridFldArr: ApiaField[];

  constructor(field: HTMLDivElement, form: ApiaForm) {
    super(field, form, [
      Field.PROPERTY_READONLY,
      Field.PROPERTY_VISIBILITY_HIDDEN,
      Field.PROPERTY_HIDE_GRID_BTN,
      Field.PROPERTY_GRID_HIDE_BTN_ORDER,
      Field.PROPERTY_GRID_HIDE_BTN_INCLUDE,
      Field.PROPERTY_GRID_HIDE_BTN_ADD,
      Field.PROPERTY_GRID_HIDE_BTN_DEL,
      Field.PROPERTY_PAGED_GRID,
      Field.PROPERTY_GRID_ALTER_IN_LAST_PAGE,
      Field.PROPERTY_INCLUDE_FIRST_ROW,
    ]);
    this.gridFldArr = [];
  }

  #dispatchEvent(event: string, props?: CustomEventInit<unknown>) {
    this.field.dispatchEvent(new CustomEvent(event, props));
  }

  addRow() {
    this.#dispatchEvent('gridAdd');
  }

  clearGrid() {
    void store.dispatch(dataGridActions.clearGrid({ gridId: this.id }));
    return this;
  }

  clearPage(pageNumber: number) {
    void store.dispatch(
      dataGridActions.clearPage({ gridId: this.id, page: pageNumber }),
    );
    return this;
  }

  clearRow(index: number) {
    const row = this.getRow(index);
    row.forEach((cell) => {
      cell.clearValue();
    });
    return this;
  }

  closeForm(): IDataGridField {
    store.dispatch(
      dataGridActions.update({ id: this.fldId as string, editionFormRow: -1 }),
    );
    return this;
  }

  get columns() {
    return this.store.columns;
  }

  get context() {
    return this.store.context.grid;
  }

  async deleteGrid() {
    await store.dispatch(
      dataGridActions.deleteRow({ gridId: this.id, rowIndex: [] }),
    );
    return this;
  }

  deletePage(pageNumber: number) {
    const props = this.store.context.grid.field.properties;
    if (props.paged) {
      const rowsPerPage = props.pagedGridSize;
      const startIndex = (pageNumber - 1) * rowsPerPage;
      const finishIndex = (pageNumber - 1) * rowsPerPage + rowsPerPage;
      const deleteRows = new Array(finishIndex - startIndex)
        .fill('')
        .map((_, i) => i + startIndex);
      void store.dispatch(
        dataGridActions.deleteRow({ gridId: this.id, rowIndex: deleteRows }),
      );
    }
  }

  deleteRow(rowIndex: number) {
    void store.dispatch(
      dataGridActions.deleteRow({ gridId: this.id, rowIndex }),
    );
    return this;
  }

  editRow(index: number) {
    store.dispatch(
      dataGridActions.update({
        id: this.fldId as string,
        editionFormRow: index,
      }),
    );
  }

  getAllColumns() {
    const columns: IApiaField[][] = [];
    const columnAttributes = this.columns.map(
      (column) => column.name || column.attName,
    );
    columnAttributes.forEach((attribute) => {
      const column = this.getColumn(attribute).filter((c): c is IApiaField =>
        Boolean(c),
      );
      columns.push(column);
    });
    return columns;
  }

  getColumn(fieldName: string) {
    const fields = [];
    for (let i = 0; i < this.rowsCount; i++) {
      fields.push(this.getField(fieldName, i));
    }
    return fields;
  }

  getCurrentPage() {
    return this.context.field.curPage;
  }

  getField(fieldName: string, fieldIndex = 0) {
    return this.form.getField(fieldName, fieldIndex);
  }

  getPageCount() {
    return this.context.field.pages;
  }

  getPageSize() {
    return this.context.field.properties.pagedGridSize;
  }

  getRow(index: number) {
    const row: IApiaField[] = [];
    const columnAttributes = this.columns.map(
      (column) => column.name || column.attName,
    );
    columnAttributes.forEach((attribute) => {
      const field = this.getField(attribute, index);
      if (field) row.push(field);
    });
    return row;
  }

  getSelectedIndexes(useAbsoluteIndex?: boolean) {
    return this.store.selectedRows.map(
      (current) =>
        current +
        (useAbsoluteIndex
          ? +noNaN(this.store.context.grid.field.startIndex)
          : 0),
    );
  }

  getSelectedItems() {
    return this.getSelectedIndexes().map((index) => this.getRow(index));
  }

  get id() {
    return this.fldId ?? '';
  }

  isPaged() {
    return this.context.field.properties.paged;
  }

  onRefresh(callback: (ev: TApiaXMLResponse) => unknown) {
    dataGridRefreshEvent.on('refresh', (ev) => {
      if (ev.gridId === this.fldId) {
        callback(ev.data);
      }
    });
  }

  get rowsCount() {
    return this.store.rows.length;
  }

  get selected_col_index() {
    return this.store.focusedColumn;
  }

  get store() {
    const id = this.fldId ?? '';
    return store.getState().dataGridSlice[id];
  }

  toggleColumnVisibility(fieldName: string, isShown?: boolean) {
    store.dispatch(
      dataGridActions.toggleColumnVisibility({
        id: this.id,
        fieldName,
        isShown,
      }),
    );
  }

  toggleDeleteButton(rowIndex: number, isShown?: boolean) {
    store.dispatch(
      dataGridActions.toggleDeleteButton({
        id: this.id,
        index: rowIndex,
        isShown,
      }),
    );
  }

  toggleEditFormButton(rowIndex: number, isShown?: boolean) {
    store.dispatch(
      dataGridActions.toggleEditFormButton({
        id: this.id,
        index: rowIndex,
        isShown,
      }),
    );
  }
  toggleInfoButton(isShown?: boolean) {
    store.dispatch(dataGridActions.toggleInfoButton({ id: this.id, isShown }));
  }
}
